import { BehaviorSubject, Observable, ReplaySubject, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthSessionService } from './auth-session.service';
import { User } from '../../models/user';
import { Profiles } from '../../modules/administration/models/profiles';
import { RedirectionContextService } from '../../modules/administration/services/redirection-context.service';
import { SessionService } from '../../modules/administration/services/session.service';
import { AccessDeniedService } from '../../modules/shared/services/access-denied.service';
import { LogService } from '../../modules/shared/services/log.service';
import { RedirectionContextToken } from '../../modules/administration/models/redirection-context-token';

/**
 * The session storage key for the access token
 */
const ACCESS_TOKEN_KEY = 'redirectionContextSessionService_accessToken';

/**
 * The session storage key for the redirection context id
 */
const REDIRECTION_CONTEXT_ID_KEY = 'redirectionContextSessionService_redirectionContextId';

export class RedirectionContextSessionService implements AuthSessionService {

  private LOG: LogService = new LogService(RedirectionContextSessionService);

  public userInfoBs: BehaviorSubject<User> = new BehaviorSubject(new User());

  constructor(
    private session: SessionService,
    private redirectionContextService: RedirectionContextService,
    private accessDeniedService: AccessDeniedService,
    accessToken: string,
    redirectionContextId: string,
    validationToken: string
  ) {
    this.accessToken = accessToken;
    this.redirectionContextId = redirectionContextId;
    this.validationToken = validationToken;
  }

  /**
   * An OAuth token used for query request
   */
  private _accessToken: string | null;

  private get accessToken(): string | null {
    if (this._accessToken === null) {
      this._accessToken = sessionStorage.getItem(ACCESS_TOKEN_KEY);
    }
    return this._accessToken;
  }

  private set accessToken(accessToken: string | null) {
    if (accessToken === null) {
      sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    } else {
      sessionStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    }
    this._accessToken = accessToken;
  }

  /**
   * The id of the redirection context used by the user to access the app
   */
  private _redirectionContextId: string | null;

  private get redirectionContextId(): string | null {
    if (this._redirectionContextId === null) {
      this._redirectionContextId = sessionStorage.getItem(REDIRECTION_CONTEXT_ID_KEY);
    }
    return this._redirectionContextId;
  }

  private set redirectionContextId(redirectionContextId: string | null) {
    if (redirectionContextId === null) {
      sessionStorage.removeItem(REDIRECTION_CONTEXT_ID_KEY);
    } else {
      sessionStorage.setItem(REDIRECTION_CONTEXT_ID_KEY, redirectionContextId);
    }
    this._redirectionContextId = redirectionContextId;
  }

  private validationToken: string;

  private _profiles$: ReplaySubject<Profiles> = new ReplaySubject(1);

  /**
   * Hold a boolean
   */
  private _ready: ReplaySubject<boolean> = new ReplaySubject(1);

  /**
   * Refresh the token
   */
  private refreshToken() {
    this.LOG.info("Refreshing redirection-context token")
    this.redirectionContextService.refreshToken(this.redirectionContextId, this.validationToken)
      .subscribe(
        (contextToken: RedirectionContextToken) => {
          this.accessToken = contextToken.access_token;
          this.validationToken = contextToken.validation_token;
          const refreshDelay = contextToken.expires_in * 1000 * 3 / 4;
          setTimeout(() => this.refreshToken(), refreshDelay); // Not this.refreshOAuthToken because this would change
        },
        error => {
          this.LOG.error("Failure to refresh redirection-context token. Keep using the current token until it's no longer valid.");
        }
      )
  }

  setAccessTokenObject(accessToken: any) {
    // no-op
  }

  public isReady(): Observable<boolean> {
    return this._ready.asObservable();
  }

  public logOut() {
    this.accessToken = null;
    this.redirectionContextId = null;
    this.validationToken = null;
    this.accessDeniedService.expiredSession();
  }

  public configure(): Observable<void> {
    return this.redirectionContextService.getRedirectionContext(this.redirectionContextId)
      .pipe(
        map(context => { this._profiles$.next(context.toProfiles()); this.refreshToken(); this._ready.next(true); }),
        catchError(error => {
          this.LOG.error('Could not retrieve redirection context ', this.redirectionContextId, ' due to error ', error)
          this._profiles$.error(new Error('Could not retrieve redirection context '))
          this._ready.next(false)
          return throwError(error)
        })
      )
  }

  public getProfiles(): Observable<Profiles> {
    return this._profiles$;
  }

  /**
   * Création des headers nécessaires pour une connexion oAuth
   * @returns l'objet header à utiliser
   */
  public getHeaders(): { [name: string]: string } {
    var headers = {};
    const headersMap: Map<string, string> = new Map();

    if (this.accessToken) {
      headersMap.set('Authorization', 'Bearer ' + this.accessToken);
    }
    if (this.redirectionContextId) {
      headersMap.set('redirection-context-id', this.redirectionContextId);
    }

    const user = this.session.getUser();
    headersMap.set('siret', user?.siret);
    headersMap.set('service', user?.service);

    headersMap.forEach((v, k) => {
      if (v) headers = Object.assign(headers, { [k]: v });
    });
    return headers;
  }

  isSwitchable(): Observable<boolean> {
    return of(false);
  }

  public logInfo() {
    this.LOG.info('======================[SESSION_INFO_BEGIN]=========================');
    this.LOG.info('[SESSION_TYPE]:', 'OAUTH');
    this.LOG.info('[ACCESS_TOKEN] :', this.accessToken)
    this.LOG.info('[REDIRECTION_CONTEXT_ID]', this.redirectionContextId)
    this.LOG.info('======================[SESSION_INFO_END]=========================')
  }
}
